import { useContext, useEffect, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router-dom";
import { Loading, Tabs, Tooltip } from "@foris/avocado-suite";
import ProgrammingEdit from "./programming-edit";
import { AppContext } from "../context/EditSessionsContext";
import LinkHeader from "@dimensions/link/components/HeaderLink";
import FormEdit from "./FormEdit/FormEdit";
import ResultEdit from "./ResultEdit/ResultEdit";
import SessionAssignationView from "./session-assignation";
import AssignmentViewSwitch from "../components/assignment-view-switch";
import AssignmentViewDiscardModal from "../components/assignment-view-discard-modal";
import ProposalModeBanner from "../components/proposal-mode-banner";
import { useAssignmentViewMode } from "../hooks/session-assignation";
import { sessionAssignationStore, useSessionAssignationStore } from "@context/sessionAssignation";
import {
  useProgrammingEditStore,
  clearProgrammingResults,
  resetProgrammingEditStore,
} from "@context/useProgrammingEditStore";
import AdvanceSearchInstructor from "./FormEdit/instructor-advanced-search";
import AdvanceSearchClassroom from "../pages/FormEdit/Classroom/AdvanceSearch";
import { useGetSessionsRecommendations } from "../hooks/useGetSessionsRecommendation";
import { Types } from "../context/linkData.reducer";
import { useProgrammingLockState } from "../hooks/useProgrammingLockState";
import { useProposalMode } from "@services/collections/assignment-proposal/assignmentProposalServices";
import { PROGRAMMING_TAB_TOOLTIP_KEYS } from "../utils/programmingLockState";
import css from "./../pages/FormEdit/formEdit.module.scss";

const InitEditSessions = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const { dispatch, state } = useContext(AppContext);
  const isProgrammingResultsActive = useProgrammingEditStore(s => s.isProgrammingResultsActive);
  const hasChanges = useProgrammingEditStore(s => s.hasChanges);
  const [
    {
      response: recommendations,
      hasErrors: hasRecommendationsErrors,
      isLoading: isLoadingRecommendations,
    },
    getRecomendations,
  ] = useGetSessionsRecommendations();
  const [heightHeader, setHeightHeder] = useState(null);
  const {
    isAssignationLocked,
    programmingTabBlockReason,
    isProgrammingReadOnly,
    canEditProgramming,
  } = useProgrammingLockState();
  const { isProposalMode, isResolving } = useProposalMode();
  // A requester must not save directly from here, and while the mode is still unknown nobody can
  // be told apart from one: the Programming tab and the sessions view stay out of reach.
  const isProposalModeOrUnknown = isProposalMode || isResolving;
  const shouldShowProgrammingTab = canEditProgramming && !isProposalModeOrUnknown;
  const assignmentView = useAssignmentViewMode(isAssignationLocked || isProposalModeOrUnknown);
  const assignmentViewSwitchId = useId();
  const sessionPhase = useSessionAssignationStore(draft => draft.phase);
  const sessionEditorDirty = useSessionAssignationStore(draft => draft.editorDirty);
  const isSessionResultsActive =
    assignmentView.view === "sessions" && ["results", "committing", "saved"].includes(sessionPhase);
  const assignmentViewSwitch = (
    <AssignmentViewSwitch
      id={assignmentViewSwitchId}
      value={assignmentView.view}
      disabled={assignmentView.disabled}
      onChange={assignmentView.requestView}
    />
  );

  /**
   * Land on the programming tab when the caller asked for it (the event details
   * modal's "Editar programación" action) or when the assignation tab is locked,
   * instead of opening on a disabled tab with no content.
   *
   * The reasons that would render the programming tab disabled are checked first:
   * the modal gates its own action on the same rules, but the two are computed
   * from separate fetches, so a stale cache there must not land the user on a
   * tab that cannot be used.
   *
   * Lazy init is enough because this component only mounts once `state.link.info`
   * has resolved (`EditSessionLayout` renders a skeleton until then), so the lock,
   * the disable reasons and the requested tab are all known on first render.
   */
  const requestedTab = (location?.state as { activeTab?: string } | undefined)?.activeTab;
  const [activeTabIndex, setActiveTabIndex] = useState(() => {
    if (!canEditProgramming) return 0;

    if (programmingTabBlockReason) return 0;

    return requestedTab === "programming" || isAssignationLocked ? 1 : 0;
  });

  // Navigation hints apply to this entry only; a refetch remounts the editor.
  useEffect(() => {
    if (!requestedTab) return;
    const remainingState = { ...(location.state as { activeTab?: string }) };
    delete remainingState.activeTab;
    history.replace({ ...location, state: remainingState });
  }, [history, location, requestedTab]);

  const isProgrammingTabDisabled =
    !!state?.page?.active?.results ||
    isSessionResultsActive ||
    (assignmentView.view === "sessions" && sessionEditorDirty) ||
    sessionPhase === "validating" ||
    !!programmingTabBlockReason;
  const programmingTabTooltipLabel = programmingTabBlockReason
    ? t(PROGRAMMING_TAB_TOOLTIP_KEYS[programmingTabBlockReason])
    : "";
  const isProgrammingTabTooltipVisible = !!programmingTabBlockReason;

  /**
   * Requets for sessions recommendations after the link info is obtained.
   */
  useEffect(() => {
    window.addEventListener("load-recommendations", (data: any) => {
      getRecomendations(data?.detail?.sessionIds);
    });

    return () => {
      window.removeEventListener("load-recommendations", (data: any) => {
        getRecomendations(data?.detail?.sessionIds);
      });
    };
  }, []);

  /** Reset the programming store when navigating away from the page entirely. */
  useEffect(() => {
    return () => {
      resetProgrammingEditStore();
      sessionAssignationStore.getState().reset();
    };
  }, []);

  useEffect(() => {
    dispatch({ type: Types.SetRecommendations, payload: recommendations });
  }, [recommendations]);

  useEffect(() => {
    dispatch({ type: Types.SetHasErrorsRecommendations, payload: hasRecommendationsErrors });
  }, [hasRecommendationsErrors]);

  useEffect(() => {
    dispatch({ type: Types.SetIsLoadingRecommendations, payload: isLoadingRecommendations });
  }, [isLoadingRecommendations]);

  const isAdvancedSearchVisible =
    state?.page?.active?.advanceSearchClassroom || state?.page?.active?.advanceSearchInstructor;

  return (
    <main className={css.main}>
      {state?.link?.info && <LinkHeader link={state?.link?.info} setHeight={setHeightHeder} />}
      {(state?.page?.loading || sessionPhase === "validating") && <Loading />}
      <AssignmentViewDiscardModal
        isOpen={!!assignmentView.pendingView}
        onCancel={assignmentView.cancelSwitch}
        onDiscard={assignmentView.confirmSwitch}
      />
      <Tabs
        className={css.editionTypeTabs}
        activeIndex={shouldShowProgrammingTab ? activeTabIndex : 0}
        onClick={index => {
          setActiveTabIndex(index);
          clearProgrammingResults();
        }}
        items={[
          {
            label: (
              <Tooltip
                className={css.assignationTooltip}
                label={t("programming-edit.tabs.assignation-locked-tooltip")}
                visible={isAssignationLocked ? undefined : false}
              >
                {t("programming-edit.tabs.assignation")}
              </Tooltip>
            ),
            disabled: isProgrammingResultsActive || isAssignationLocked,
            // Tabs renders the children of the active tab even when it is disabled, so a locked
            // assignation (applied programming structure) must not render the editor while the
            // Programming tab is out of reach: in proposal mode only the notice of why nothing can
            // be proposed on the link.
            children:
              isAssignationLocked && isProposalModeOrUnknown ? (
                <ProposalModeBanner />
              ) : (
                <>
                  {assignmentView.view === "sessions" && (
                    <SessionAssignationView
                      heightHeader={heightHeader}
                      viewSwitch={assignmentViewSwitch}
                      isAssignationLocked={isAssignationLocked}
                    />
                  )}
                  {assignmentView.view === "traditional" &&
                    [state?.page?.active?.form, isAdvancedSearchVisible].includes(true) && (
                      <div className={isAdvancedSearchVisible && css.contentHidden}>
                        <FormEdit heightHeader={heightHeader} viewSwitch={assignmentViewSwitch} />
                      </div>
                    )}
                  {state?.page?.active?.results && <ResultEdit heightHeader={heightHeader} />}
                  {state?.page?.active?.advanceSearchClassroom && <AdvanceSearchClassroom />}
                  {state?.page?.active?.advanceSearchInstructor && <AdvanceSearchInstructor />}
                </>
              ),
          },
          ...(shouldShowProgrammingTab
            ? [
                {
                  disabled: isProgrammingTabDisabled,
                  label: (
                    <Tooltip
                      label={programmingTabTooltipLabel}
                      visible={isProgrammingTabTooltipVisible ? undefined : false}
                    >
                      {t("programming-edit.tabs.programming")}
                      {hasChanges && <span className={css.programmingChangesDot} />}
                    </Tooltip>
                  ),
                  children: isProgrammingResultsActive ? (
                    <ResultEdit heightHeader={heightHeader} />
                  ) : (
                    <ProgrammingEdit isReadOnly={isProgrammingReadOnly} />
                  ),
                },
              ]
            : []),
        ]}
      />
    </main>
  );
};

export default InitEditSessions;
